import React, { useMemo } from "react";
import styles from "./ActiveQuestsCard.module.scss";
import { GiArcheryTarget } from "react-icons/gi";
import { IoGameControllerOutline } from "react-icons/io5";
import { useSelector } from "react-redux";
import { IStore } from "@/redux/defaultStore";
import { useGetAllQuestConfigsForUser } from "@/hooks/useGetAllQuestConfigsForUser";
import { useGetQuestProgressionForUser } from "@/hooks/useGetQuestProgressionForUser";
import Skeleton from "@/components/skeletons/Skeleton";

interface ActiveQuestsCardProps {
    onViewAllClick?: () => void;
}

export const ActiveQuestsCard: React.FC<ActiveQuestsCardProps> = ({
    onViewAllClick,
}) => {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const isAuthenticated = !!fullToken;

    // Fetch all quest configs
    const { data: questConfigs, isLoading: isLoadingConfigs } = useGetAllQuestConfigsForUser(
        fullToken,
        isAuthenticated ? {} : undefined,
    );

    // Get quest IDs for progression fetch
    const questConfigIds = questConfigs?.map(q => q.id).filter(Boolean) || [];

    // Fetch quest progressions
    const { data: questProgressions, isLoading: isLoadingProgressions } = useGetQuestProgressionForUser(
        fullToken,
        questConfigIds,
    );

    const isLoading = isLoadingConfigs || isLoadingProgressions;

    // Calculate summary stats from new quest system data
    const summary = useMemo(() => {
        if (!questConfigs || !questProgressions) {
            return {
                claimableCount: 0,
                activeCount: 0,
                totalAvailableXP: 0,
                overallProgress: 0,
                uniqueGames: 0,
            };
        }

        let claimableCount = 0;
        let activeCount = 0;
        let totalAvailableXP = 0;
        let totalProgress = 0;
        let totalTarget = 0;
        const gameSet = new Set<string>();

        questConfigs.forEach((questConfig) => {
            const progression = questProgressions.find((prog) => prog?.questConfigId === questConfig?.id);

            // Track unique games
            if (questConfig.game?._id) {
                gameSet.add(questConfig.game._id);
            }

            // Calculate progress from milestones
            let questProgress = 0;
            let questTarget = 0;
            let hasClaimableMilestone = false;
            const isClaimed = progression?.claimedAt ?? false;
            const isComplete = progression?.isComplete ?? false;

            if (questConfig.milestones && questConfig.milestones.length > 0) {
                questConfig.milestones.forEach(milestone => {
                    const milestoneProgression = progression?.progression?.[milestone.id];
                    if (milestoneProgression) {
                        questProgress += milestoneProgression.amount || 0;
                        questTarget += milestone.targetAmount || 0;
                        if (!milestoneProgression.isComplete && (milestoneProgression.amount || 0) >= (milestone.targetAmount || 0)) {
                            hasClaimableMilestone = true;
                        }
                    } else {
                        questTarget += milestone.targetAmount || 0;
                    }
                });
            }

            const isClaimable = hasClaimableMilestone || (isComplete && !isClaimed);

            // Count claimable vs active
            if (isClaimable && !isClaimed) {
                claimableCount++;
                // TODO: Add XP calculation from reward configs when available
                totalAvailableXP += 0; // Placeholder
            } else if (!isClaimed) {
                activeCount++;
            }

            // Track overall progress
            totalProgress += questProgress;
            totalTarget += questTarget;
        });

        const overallProgress = totalTarget > 0 ? Math.round((totalProgress / totalTarget) * 100) : 0;

        return {
            claimableCount,
            activeCount,
            totalAvailableXP,
            overallProgress,
            uniqueGames: gameSet.size,
        };
    }, [questConfigs, questProgressions]);

    // Calculate stroke-dashoffset for the progress ring
    const radius = 40;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - summary.overallProgress / 100 * circumference;

    // Show loading state
    if (isLoading) {
        return (
            <div className={styles.activeQuestsCard}>
                <div className={styles.header}>
                    <div className={styles.titleGroup}>
                        <span className={styles.iconWrapper}>
                            <GiArcheryTarget />
                        </span>
                        <h3>Active Quests</h3>
                    </div>
                </div>
                <Skeleton height={200} rows={4} />
            </div>
        );
    }

    // Show unauthenticated state
    if (!isAuthenticated) {
        return (
            <div className={styles.activeQuestsCard}>
                <div className={styles.header}>
                    <div className={styles.titleGroup}>
                        <span className={styles.iconWrapper}>
                            <GiArcheryTarget />
                        </span>
                        <h3>Active Quests</h3>
                    </div>
                </div>
                <div className={styles.content}>
                    <div className={styles.emptyState}>
                        <div className={styles.emptyIcon}>🎯</div>
                        <h4>Quest Rewards Awaiting!</h4>
                        <p>Sign in to see your active quests, track completion progress, and claim XP rewards.</p>
                    </div>
                </div>
            </div>
        );
    }

    // Don't show empty state - just show zeros in the regular UI

    return (
        <div className={styles.activeQuestsCard}>
            <div className={styles.header}>
                <div className={styles.titleGroup}>
                    <span className={styles.iconWrapper}>
                        <GiArcheryTarget />
                    </span>
                    <h3>Active Quests</h3>
                </div>
            </div>

            <div className={styles.content}>
                <div className={styles.progressRing}>
                    <svg width="100" height="100" viewBox="0 0 100 100">
                        <defs>
                            <linearGradient id="questProgressGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" stopColor="#7859E6" />
                                <stop offset="100%" stopColor="#FF4238" />
                            </linearGradient>
                        </defs>

                        {/* Background circle */}
                        <circle
                            cx="50"
                            cy="50"
                            r={radius}
                            stroke="rgba(255, 255, 255, 0.1)"
                            strokeWidth="6"
                            fill="none"
                        />

                        {/* Progress circle */}
                        <circle
                            cx="50"
                            cy="50"
                            r={radius}
                            stroke="url(#questProgressGradient)"
                            strokeWidth="6"
                            fill="none"
                            strokeLinecap="round"
                            strokeDasharray={circumference}
                            strokeDashoffset={strokeDashoffset}
                            transform="rotate(-90 50 50)"
                            className={styles.progressCircle}
                        />
                    </svg>

                    <div className={styles.progressText}>
                        <span className={styles.percentage}>{summary.overallProgress}%</span>
                    </div>
                </div>

                <div className={styles.statsGrid}>
                    <div className={styles.statItem}>
                        <span className={styles.statValue}>{summary.claimableCount}</span>
                        <span className={styles.statLabel}>Claimable</span>
                    </div>

                    <div className={styles.statItem}>
                        <span className={styles.statValue}>{summary.activeCount}</span>
                        <span className={styles.statLabel}>Active</span>
                    </div>

                    <div className={styles.statItem}>
                        <span className={styles.statValue}>{summary.totalAvailableXP.toLocaleString()}</span>
                        <span className={styles.statLabel}>XP Avail</span>
                    </div>
                </div>

                {summary.claimableCount > 0
                    ? <button
                        className={styles.actionButton}
                        onClick={onViewAllClick}
                    >
                        Claim {summary.claimableCount} Reward{summary.claimableCount !== 1 ? "s" : ""}
                    </button>
                    : <button
                        className={styles.actionButton}
                        onClick={onViewAllClick}
                    >
                        View All Quests
                    </button>
                }

                <span className={styles.subtext}>
                    Across {summary.uniqueGames} Game{summary.uniqueGames !== 1 ? "s" : ""}
                </span>
            </div>
        </div>
    );
};